import React, { useEffect, useState, useRef } from 'react';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { AccountType, User, SystemSettings } from '../types';
import { Icons, THEME } from '../constants';

interface LayoutProps {
  children: React.ReactNode;
  activePage: string;
  setActivePage: (page: string, initialView?: any) => void;
  user: User;
  activeAccountType: AccountType;
  setActiveAccountType: (type: AccountType) => void;
  onDepositClick: () => void;
  systemSettings: SystemSettings | null;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  activePage,
  setActivePage,
  user,
  activeAccountType,
  setActiveAccountType,
  onDepositClick,
  systemSettings
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [demoFlash, setDemoFlash] = useState<'profit' | 'loss' | null>(null);
  const [liveFlash, setLiveFlash] = useState<'profit' | 'loss' | null>(null);
  const [hasUnreadSupport, setHasUnreadSupport] = useState(false);
  const prevDemoRef = useRef(user.demoBalance);
  const prevLiveRef = useRef(user.liveBalance);

  const siteInitial = systemSettings?.siteName?.[0] || 'Y';

  useEffect(() => {
    if (user.demoBalance !== prevDemoRef.current) {
      setDemoFlash(user.demoBalance > prevDemoRef.current ? 'profit' : 'loss');
      setTimeout(() => setDemoFlash(null), 1000);
      prevDemoRef.current = user.demoBalance;
    }
    if (user.liveBalance !== prevLiveRef.current) {
      setLiveFlash(user.liveBalance > prevLiveRef.current ? 'profit' : 'loss');
      setTimeout(() => setLiveFlash(null), 1000);
      prevLiveRef.current = user.liveBalance;
    }
  }, [user.demoBalance, user.liveBalance]);

  // Real-time listener for user notifications
  useEffect(() => {
    if (!user.uid) return;
    const q = query(
      collection(db, 'supportTickets'), 
      where('userId', '==', user.uid), 
      where('hasUnreadUser', '==', true)
    );
    const unsub = onSnapshot(q, (snap) => {
      setHasUnreadSupport(!snap.empty);
    });
    return () => unsub();
  }, [user.uid]);

  const navItems = [
    { id: 'chart', label: 'Dashboard', icon: Icons.Chart },
    { id: 'deals', label: 'Trade Log', icon: Icons.Deals },
    { id: 'robot', label: 'Ai Robot', icon: Icons.Robot },
    { id: 'support', label: 'Support', icon: Icons.Support, hasBadge: hasUnreadSupport },
    { id: 'account', label: 'Profile', icon: Icons.Account },
    { id: 'about', label: 'About Us', icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
    ) },
  ];

  if (user.role === 'admin') {
    navItems.push({ id: 'admin', label: 'Admin', icon: () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><path d="M7 7h.01"/><path d="M7 12h.01"/><path d="M7 17h.01"/><path d="M11 7h6"/><path d="M11 12h6"/><path d="M11 17h6"/></svg>
    ), hasBadge: false });
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-bg text-white flex-col grid-bg">
      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Drawer Menu */}
      <aside className={`fixed top-0 left-0 h-full w-[280px] bg-surface border-r border-border z-[101] transition-transform duration-300 transform ${isDrawerOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col`}>
        <div className="p-8 border-b border-border flex flex-col items-center gap-4">
          <div className="w-20 h-20 rounded-full border-2 border-[#AEB877] p-1">
            <div className="w-full h-full rounded-full bg-surface-light flex items-center justify-center overflow-hidden">
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.fullName}`} alt="Avatar" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="text-center">
            <h3 className="font-bold text-lg">{user.fullName}</h3>
            <p className="text-xs text-muted uppercase tracking-widest mt-1">Ref - {user.uid.slice(0, 6).toUpperCase()}</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-6 no-scrollbar">
          <div className="px-6 mb-4">
            <p className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] mb-4">Main Menu</p>
            <div className="space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActivePage(item.id);
                    setIsDrawerOpen(false);
                  }}
                  className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${
                    activePage === item.id ? 'bg-primary/10 text-primary' : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <div className="shrink-0">
                    <item.icon />
                  </div>
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.hasBadge && (
                    <span className="ml-auto w-2 h-2 bg-[#FF4D4D] rounded-full shadow-[0_0_8px_#FF4D4D]"></span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="px-6 mt-8">
            <p className="text-[10px] font-bold text-muted uppercase tracking-[0.2em] mb-4">History</p>
            <div className="space-y-1">
              <button onClick={() => { setActivePage('account', 'transaction-history'); setIsDrawerOpen(false); }} className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-all">
                <Icons.Deals />
                <span className="text-sm font-medium">Deposit History</span>
              </button>
              <button onClick={() => { setActivePage('account', 'transaction-history'); setIsDrawerOpen(false); }} className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-all">
                <Icons.Deals />
                <span className="text-sm font-medium">Withdraw History</span>
              </button>
            </div>
          </div>
        </nav>

        <div className="p-6 border-t border-border">
          <button 
            onClick={() => { /* Logout logic is in App.tsx but we can trigger it via a prop if needed, or just use Account page logout */ setActivePage('account'); setIsDrawerOpen(false); }}
            className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-red-500/60 hover:bg-red-500/10 hover:text-red-500 transition-all"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            <span className="text-sm font-medium">Log Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col relative overflow-hidden h-full">
        <header className="h-20 flex items-center justify-between px-6 md:px-12 z-10 border-b border-border bg-bg/80 backdrop-blur-xl shrink-0">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setIsDrawerOpen(true)}
              className="p-2 hover:bg-white/5 rounded-lg transition-all"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => setActivePage('chart')}>
              <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-black font-black text-lg">{siteInitial}</span>
              </div>
              <span className="font-black text-lg tracking-tighter text-white uppercase">{systemSettings?.siteName || 'TRADING'}</span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button 
              onClick={onDepositClick}
              className="hidden md:flex bg-primary text-black px-4 py-2 rounded-lg font-black text-[10px] uppercase tracking-wider hover:bg-white transition-all shadow-lg shadow-primary/20"
            >
              Deposit
            </button>

            <div className="hidden md:flex items-center gap-4 mr-4">
              <div className="flex flex-col items-end">
                <span className="text-[10px] font-bold text-primary glow-text uppercase tracking-widest">Live Balance</span>
                <span className="text-sm font-bold">${user.liveBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
            
            <button 
              onClick={onDepositClick}
              className="md:hidden w-10 h-10 bg-primary text-black rounded-full flex items-center justify-center shadow-lg shadow-primary/20"
            >
              <Icons.Deposit />
            </button>

            <button 
              onClick={() => setActivePage('account')}
              className="w-10 h-10 rounded-full border border-border p-0.5 hover:border-primary transition-all"
            >
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.fullName}`} alt="Profile" className="w-full h-full rounded-full object-cover" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto relative no-scrollbar pt-8 px-6 md:px-12">
          <div className="max-w-7xl mx-auto w-full h-full">
            {children}
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav - Replaced by Drawer but keeping for quick access if needed, or removing to match images */}
      {/* The images don't show a bottom nav, they use the drawer. I'll remove it for accuracy. */}
    </div>
  );
};

export default Layout;